import { useLayoutEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { getApiErrorMessage } from "@/api";
import { Subtitle } from "@/components/dialog/DialogLayout";
import { FormFieldError } from "@/components/form/FormField";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { UserListItem } from "@/components/user/UserListItem";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import type { MemberRole } from "@/models/api/MemberApi";
import type { MemberModel } from "@/models/MemberModel";
import type { TeamModel } from "@/models/TeamModel";

const MemberRoleDropdown = observer(function MemberRoleDropdown({
  member,
  activeRoleKey,
  onError,
}: {
  member: MemberModel;
  activeRoleKey: MemberRole;
  onError: (error: string) => void;
}) {
  const {
    auth,
    dialog: {
      teamDialog: { team },
    },
  } = useStore();
  const t = useTranslations();
  const [showSpinner, setShowSpinner] = useState(false);
  // mirrors the backend grant_and_revoke_access policy (owner/admin only)
  const currentUserRole = team?.membersUX.currentUserRole;
  const userCanUpdateMemberRole = currentUserRole === "owner" || currentUserRole === "admin";
  const userIsMember = auth.currentUser?.username === member.user.username;

  const handleChangeRoleClick = async (role: MemberRole) => {
    onError("");
    setShowSpinner(true);
    try {
      await member.updateRole(role);
    } catch (err) {
      onError(getApiErrorMessage(err));
    }
    setShowSpinner(false);
  };

  const handleRemoveMemberClick = async () => {
    onError("");
    setShowSpinner(true);
    try {
      await member.remove();
    } catch (err) {
      onError(getApiErrorMessage(err));
    }
    setShowSpinner(false);
  };

  // Ownership cannot be granted or revoked through this endpoint, so owner is
  // displayed as the current role but is never offered as a target.
  const roles = [
    {
      key: "owner",
      title: t("teamDialog.statusOwner"),
      isActive: activeRoleKey === "owner",
    },
    {
      key: "admin",
      title: t("teamDialog.statusAdmin"),
      isActive: activeRoleKey === "admin",
      onClick: () => handleChangeRoleClick("admin"),
    },
    {
      key: "member",
      title: t("teamDialog.statusMember"),
      isActive: activeRoleKey === "member",
      onClick: () => handleChangeRoleClick("member"),
    },
    // {
    //   key: "viewer",
    //   title: t("teamDialog.statusViewer"),
    //   isActive: activeRoleKey === "guest",
    //   onClick: () => handleChangeRoleClick("guest"),
    // },
  ];

  const items = [
    ...roles
      .filter((role) => role.key !== "owner")
      .map((role) => ({
        ...role,
        active: role.isActive,
        title: role.title,
      })),
    {
      title: "",
      component: (
        <Sidebar.Button
          inverted
          className={twMerge(
            "relative rounded-8 h-8.5 min-h-0 mt-3 px-1.25",
            "data-[focus]:bg-sidebar-button-hover-bg",
          )}
          onClick={handleRemoveMemberClick}
        >
          <div className="absolute -top-1.5 left-0 w-full border-t border-gray-200 dark:border-gray-800 pointer-events-none" />
          <div className="flex flex-1 items-start truncate text-red-500 text-[15px] leading-[18px] tracking-tight">
            {t("teamDialog.remove")}
          </div>
        </Sidebar.Button>
      ),
    },
  ];

  const activeRole = roles.find((i) => i.isActive);

  // An owner's membership cannot be changed or removed, so their row has no
  // actions available to anyone.
  if (!userCanUpdateMemberRole || userIsMember || activeRoleKey === "owner") {
    return (
      <div
        className={twMerge(
          "flex h-9.5 px-2.5 text-gray-500 text-[15px] leading-[1.2] text-right tracking-tight",
          "sm:text-[17px]",
        )}
      >
        <div className="m-auto">{activeRole?.title}</div>
      </div>
    );
  }

  if (showSpinner) {
    return (
      <div className="flex h-9.5">
        <Icon name="Spinner" className="m-auto animate-spin" />
      </div>
    );
  }

  const toggleText = activeRole?.title ?? "";

  return (
    <Dropdown
      anchor="bottom end"
      toggleText={toggleText}
      toggleTextInvertedBg
      items={items}
      itemsClassName="min-w-[200px]"
    />
  );
});

const Member = observer(function Member(props: { member: MemberModel }) {
  const { member } = props;
  const { user, role } = member;
  const [serverError, setServerError] = useState("");
  const { auth } = useStore();
  const t = useTranslations();
  const userIsMember = auth.currentUser?.username === member.user.username;

  const handleError = (error: string) => {
    setServerError(error);
  };

  return (
    <div role="listitem" aria-label={user.name} className="relative">
      <UserListItem
        isLink
        user={user}
        namePostfix={userIsMember ? `(${t("teamDialog.itIsYou")})` : ""}
        rightController={
          <MemberRoleDropdown member={member} activeRoleKey={role} onError={handleError} />
        }
      />
      {serverError && <FormFieldError message={serverError} className="mt-0 mb-1.25" />}
    </div>
  );
});

export const MemberList = observer(function MemberList({ team }: { team: TeamModel }) {
  const { membersUX } = team;
  const { members } = membersUX;
  const t = useTranslations();

  useLayoutEffect(() => {
    team.membersUX.loadMembers().catch((err) => {
      console.error("Failed to load team members:", err);
    });
  }, [team]);

  const renderMember = (member: MemberModel) => {
    return <Member key={member.user.username} member={member} />;
  };

  if (membersUX.membersStatus === "loading") {
    return (
      <div className="flex items-center justify-center p-10">
        <Icon name="Spinner" className="animate-spin" />
      </div>
    );
  }

  if (members.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <Subtitle title={t("teamDialog.membersTitle")} className="mb-2.5" />
      <div role="list" aria-label={t("teamDialog.membersTitle")}>
        {members.map(renderMember)}
      </div>
    </div>
  );
});
